/* ExternalConnectionCrudCard.tsx
    Une card affichant la gestion des connexions externes
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import {
	ExternalConnectionData,
	externalConnectionsApi,
} from 'features/externalConnection';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface ExternalConnectionsCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
	/** Force l'affichage de faGlobe même en taille lg/md. Utile quand la card est intégrée dans une page qui ne possède pas son propre WidgetHeader. Défaut : false */
	forceIcon?: boolean;
}

// ExternalConnectionCrudCard component
export const ExternalConnectionCrudCard: React.FC<
	ExternalConnectionsCrudCardProps
> = ({
	size = 'sm',
	returnPath = '/app/settings/infrastructure',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
	forceIcon = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [
					{
						label: 'ID',
						field: 'id',
						render: (a: ExternalConnectionData) => a.id,
					},
				]
			: []),
		// Colonne "Nom"
		{
			label: 'Nom',
			field: 'name',
			render: (a: ExternalConnectionData) => a.name,
		},
		// Colonne "Driver" uniquement si extended
		...(extended
			? [
					{
						label: 'Driver',
						field: 'driver',
						render: (a: ExternalConnectionData) => a.driver ?? '',
					},
				]
			: []),
		// Colonne "Host"
		{
			label: 'Hôte',
			field: 'host',
			render: (a: ExternalConnectionData) => (a.host ? a.host : ''),
		},
		// Colonne "Port" uniquement si extended
		...(extended
			? [
					{
						label: 'Port',
						field: 'port',
						render: (a: ExternalConnectionData) => a.port ?? '',
					},
				]
			: []),
		// Colonne "lastSync" — tri désactivé (valeur calculée)
		{
			label: 'Dernière synchronisation',
			field: 'lastSync',
			render: (a: ExternalConnectionData) =>
				a.lastSync
					? new Date(a.lastSync).toLocaleDateString('fr-FR')
					: 'Non synchronisé',
		},
	];

	return (
		<GenericCrudCard<ExternalConnectionData>
			queryKey="externalConnections"
			title={
				size === 'sm' ? 'Connexions externes' : 'Liste des connexions externes'
			}
			fetchData={(token, options) =>
				externalConnectionsApi.listPaginated(token, options)
			}
			deleteItem={externalConnectionsApi.delete}
			tableColumns={columns}
			addButtonLabel="Ajouter une connexion externe"
			getAddPath={() => '/app/settings/externalConnections/new'}
			getEditPath={(id) => `/app/settings/externalConnections/${id}/edit`}
			emptyMessage="Aucune connexion externe trouvée."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' || forceIcon ? faGlobe : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default ExternalConnectionCrudCard;
